import { Box, HStack, Flex, Text } from '@televet/kibble-ui';
import React, { useCallback, useMemo } from 'react';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import { SidePanelParentPetDataFragment, useCheckPetEnrollmentSubscription } from 'shared/types/graphql';
import { titleCase } from 'shared/utils';
import { useSidePanelSearch } from '../../../state/providers/SearchPanelProvider';
import { FeatureFlagName } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { getSubscriptionPaymentStatus } from '../../Benefits/utils/careEnrollments';

interface PetsListItemProps {
  pet: SidePanelParentPetDataFragment;
  petParentId: string;
  refetchPets: () => Promise<void>;
}

export const PetsListItem = ({ pet, refetchPets }: PetsListItemProps): JSX.Element => {
  const { viewClinicPet } = useSidePanelSearch();
  const { setCurrentClinicPetId } = useSidePanelSearch();
  const { isFeatureEnabled } = useFeatureFlag();
  const { name, id, organizationPet } = pet;

  const goToPet = useCallback(
    (id?: string): void => {
      if (id) {
        viewClinicPet({ clinicPetId: id });
        setCurrentClinicPetId(pet?.id);
      }
    },
    [viewClinicPet, pet?.id, setCurrentClinicPetId],
  );

  useCheckPetEnrollmentSubscription({
    variables: {
      where: {
        organizationPetId: pet.organizationPet?.id || '',
      },
    },
    skip: !pet.organizationPet?.id,
    onData: ({ data }) => {
      if (data.data?.checkPetEnrollment?.isEnrolled) {
        refetchPets();
      }
    },
  });

  const isCareEnabled = useMemo(() => isFeatureEnabled(FeatureFlagName.CarePlans), [isFeatureEnabled]);
  const careSubscriptionPaymentStatus = useMemo(() => getSubscriptionPaymentStatus(organizationPet), [organizationPet]);

  return (
    <Flex
      data-mixpanel-name="Pet icon in pet parent profile"
      data-testid="pet-parent-view-pet-button"
      key={id}
      onClick={(): void => goToPet(id)}
      alignItems="center"
      justifyContent="flex-start"
      py={2}
      px={6}
      cursor="pointer"
      _hover={{ bgColor: 'background.alpha.hover' }}
    >
      <PetAvatar
        speciesType={pet.speciesType}
        photoUrl={pet.photoUrl}
        name={pet.name}
        isDeceased={pet.computedIsDeceased}
        isActive={pet.computedIsActive}
        {...(isCareEnabled && {
          careStatus: careSubscriptionPaymentStatus,
        })}
      />
      <Box mr={3} onClick={(): void => goToPet(id)}>
        <HStack alignItems="center" justify="center">
          <Text fontWeight="bold" as="span">
            {titleCase(name) || 'No Name Provided'}
          </Text>
        </HStack>
      </Box>
    </Flex>
  );
};
